import { depthOf } from './json/depth';
import { tokenize } from './json/lexer';
import { parseTree } from './json/parse';
import type { JsonNode } from './json/tree';
import type { Token } from './json/tokens';
import { createLintReport, runLintChecks } from './lint/engine';
import type { Diagnostic, Fix } from './lint/diagnostic';
import { DEFAULT_LINT_OPTIONS, type LintOptions } from './lint/options';
import type { LintReport } from './lint/report';
import { ALL_REPAIR_RULES } from './repair/registry';
import type { RepairContext, RepairProposal, RepairRule } from './repair/types';
import { createLineIndex, type LineIndex } from './text/lineIndex';
import { utf8ByteLength } from './text/size';

/**
 * The in-process pipeline result, shared by lint, repair, format and future tabs.
 *
 * Rich where it is cheap — in process. It NEVER crosses `postMessage`: that is what
 * `LintReport` is for (ADR-0010). Because it never crosses, `lines` is allowed to be an
 * object with methods.
 */
export interface AnalyzedDocument {
  readonly source: string;
  /** Including trivia — repair rules need it. */
  readonly tokens: readonly Token[];
  /** `undefined` when the depth guard fired. */
  readonly tree: JsonNode | undefined;
  readonly lines: LineIndex;
  readonly report: LintReport;
}

/**
 * A clock that exists in a program with `lib: ["ES2022"]` and `types: []` (gate 1).
 *
 * `performance` is a host global, not an ES one, so it cannot be named directly here. It is
 * present in every browser and in Node ≥ 16 and is monotonic, which `Date.now()` is not —
 * a clock adjustment mid-analysis would otherwise be able to report a negative `elapsedMs`.
 * `Date` satisfies the same shape and is the fallback.
 */
interface ClockLike {
  now(): number;
}
const clock: ClockLike =
  typeof (globalThis as { performance?: Partial<ClockLike> }).performance?.now === 'function'
    ? (globalThis as unknown as { performance: ClockLike }).performance
    : Date;

/**
 * `analyzeDocument` — the single in-process pipeline, exactly the eight steps of ADR-0007.
 *
 * ```
 * 0. precheck    size guard              ← this file
 * 1. lex         tokenize()              ← json/lexer.ts
 * 2. depthScan   depthOf()               ← json/depth.ts
 * 3. parse       parseTree()             ← json/parse.ts   (skipped past maxDepth)
 * 4-6. checks    structure/tokens/semantics via runLintChecks()
 * 7. finalise    positions, severity, sort, dedup, cap     ← lint/engine.ts
 *    …plus quick-fix binding, which is the half of step 7 that lives here.
 * ```
 *
 * There is exactly ONE of these in the repository, which is the point: `lint()`,
 * `format()`, `repair()`, the Worker and every future tab all enter here, so there is no
 * second, weaker opinion anywhere about whether a document is valid.
 *
 * IT DOES NOT THROW. Not on a 40 MiB paste, not on a lone surrogate, not on 100 000 nested
 * brackets. Every refusal comes back as a `Diagnostic`, because the caller is a text editor
 * that has to keep running and keep the user's text.
 */
export function analyzeDocument(source: string, options?: Partial<LintOptions>): AnalyzedDocument {
  const resolved: LintOptions = { ...DEFAULT_LINT_OPTIONS, ...options };
  const startedAt = clock.now();

  // ── step 0: precheck ─────────────────────────────────────────────────────────────────
  //
  // The size guard runs before a single token is allocated. Past the limit we do not lex,
  // do not parse and do not build a line index over ten megabytes: the whole reason the
  // limit exists is that the work is what freezes the tab. The document comes back with no
  // tokens, no tree, and one `error` diagnostic saying so — so `report.ok` is false,
  // `formatAnalyzed` refuses, and the caller is told rather than left waiting.
  //
  // `utf8ByteLength` and not `source.length`: the limit is about bytes the user pasted, and
  // for a CJK or emoji document the two differ by up to 3×.
  if (utf8ByteLength(source) > resolved.maxBytes) {
    return refused(source, clock.now() - startedAt);
  }

  // ── steps 1-3 ────────────────────────────────────────────────────────────────────────
  const tokens = tokenize(source);
  const lines = createLineIndex(source);
  // Measured once over the iterative token stream and handed to `parseTree`, which would
  // otherwise scan the document a second time to make the same decision.
  const depth = depthOf(tokens);
  const parsed = parseTree(source, { depth, maxDepth: resolved.maxDepth });

  // ── steps 4-7 ────────────────────────────────────────────────────────────────────────
  const run = runLintChecks({
    source,
    tokens,
    tree: parsed.tree,
    rawErrors: parsed.rawErrors,
    lines,
    options: resolved,
  });

  const diagnostics = bindFixes(run.diagnostics, source, tokens, parsed.tree, lines);

  return {
    source,
    tokens,
    tree: parsed.tree,
    lines,
    report: createLintReport({ diagnostics, truncated: run.truncated }, clock.now() - startedAt),
  };
}

/** `lint(source, options?)` — the report alone, for a caller that does not need the CST. */
export function lint(source: string, options?: Partial<LintOptions>): LintReport {
  return analyzeDocument(source, options).report;
}

/**
 * The `AnalyzedDocument` of a document we refused to look at.
 *
 * `tokens` is empty rather than partial, and `tree` is `undefined`, because a half-analysis
 * is worse than none: a repair rule handed the first 10 MiB of tokens would happily insert
 * a closing brace at a boundary it invented.
 */
function refused(source: string, elapsedMs: number): AnalyzedDocument {
  // Indexed against the EMPTY string, not against `source`: scanning ten megabytes for line
  // starts is work, and the one diagnostic below sits at 1:1 where there is nothing to
  // index. Consumers get a `LineIndex` that is honest about having no content rather than
  // one that cost as much as the analysis we just declined to run.
  const lines = createLineIndex('');
  const diagnostic: Diagnostic = {
    code: 'internal/analysis-aborted',
    severity: 'error',
    span: { from: 0, to: 0 },
    start: { line: 1, column: 1 },
    end: { line: 1, column: 1 },
    params: { reason: 'too-large' },
  };

  return {
    source,
    tokens: [],
    tree: undefined,
    lines,
    report: {
      ok: false,
      diagnostics: [diagnostic],
      counts: { errors: 1, warnings: 0, infos: 0, fixable: 0 },
      truncated: false,
      elapsedMs,
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────────────────
// Step 7b: quick-fix binding.
// ────────────────────────────────────────────────────────────────────────────────────────

/**
 * Attach a `Fix` to every diagnostic that exactly one repair rule closes on its own.
 *
 * THIS IS THE SEAM between lint and repair, and it lives here rather than in
 * `lint/engine.ts` for a structural reason: the lint engine must not depend on the repair
 * registry (a check that could reach the rules could start emitting diagnostics *because* a
 * fix exists), and the repair engine must not depend on the lint engine (its driver is
 * tested against a fake rule set). `analyze.ts` is the one file that is allowed to know
 * both, so the join happens once, here.
 *
 * "Exactly one" is literal. Where two rules both offer to close the same diagnostic the
 * diagnostic gets NO `fix`: a one-click button that picks between two readings of the
 * user's intent is the legacy behaviour this codebase exists to delete. Those proposals are
 * still reachable through `RepairEngine.proposalsFor`, which is what the quick-fix *menu*
 * uses; `Diagnostic.fix` is only the single-click affordance (ADR-0012).
 *
 * COST — this is the most expensive thing in the pipeline, and it is bounded twice.
 *
 * It is SKIPPED outright on a document with no `error`: there is nothing to fix, and that is
 * the common case (and the one the 2 MiB perf budget measures), so it costs nothing at all
 * on a healthy document.
 *
 * When it does run, the matching is a two-pointer sweep rather than a filter per diagnostic.
 * The naive version — `proposals.filter(touches)` once per diagnostic — is 500 × 200 000 on a
 * document of 200 000 stray commas, and it measured 1.6 seconds of pure matching. Each bucket
 * is sorted by position below and the lint engine's sort puts the diagnostics in the same
 * order, so a pointer that only ever moves forward visits each proposal once. Measured over
 * 600 KiB of `1,,`: `lint()` 2 116 ms → 424 ms, with a 2 MiB document carrying one stray
 * comma at 274 ms and the same document clean at 185 ms.
 *
 * An earlier version of this gate skipped binding whenever the report was `truncated`, and
 * that was wrong in a way worth recording: the perf fixture is full of 20-digit integers, so
 * a 2 MiB document with ONE misplaced comma still truncates on `number/precision-loss`
 * warnings, and every quick fix vanished from a document with a single defect. A performance
 * gate that keys on a symptom instead of the cost will find the wrong documents.
 *
 * `counts.fixable` is derived from what is actually attached, never guessed from the code: a
 * code being *repairable in principle* is not this occurrence having a safe edit (ADR-0012).
 */
interface Candidate {
  readonly rule: RepairRule;
  readonly proposal: RepairProposal;
  /** The whole region the proposal reads or writes — span and every edit. */
  readonly lo: number;
  readonly hi: number;
}

function bindFixes(
  diagnostics: readonly Diagnostic[],
  source: string,
  tokens: readonly Token[],
  tree: JsonNode | undefined,
  lines: LineIndex,
  rules: readonly RepairRule[] = ALL_REPAIR_RULES,
): readonly Diagnostic[] {
  if (!diagnostics.some((d) => d.severity === 'error')) return diagnostics;

  const ctx: RepairContext = Object.freeze({ source, tokens, tree, diagnostics, lines });

  const byCode = new Map<string, Candidate[]>();
  for (const rule of rules) {
    for (const proposal of rule.detect(ctx)) {
      let lo = proposal.span.from;
      let hi = proposal.span.to;
      for (const edit of proposal.edits) {
        lo = Math.min(lo, edit.from);
        hi = Math.max(hi, edit.to);
      }

      const bucket = byCode.get(proposal.resolves);
      const candidate: Candidate = { rule, proposal, lo, hi };
      if (bucket === undefined) byCode.set(proposal.resolves, [candidate]);
      else bucket.push(candidate);
    }
  }
  if (byCode.size === 0) return diagnostics;

  for (const bucket of byCode.values()) bucket.sort((a, b) => a.lo - b.lo || a.hi - b.hi);

  // One forward pointer per bucket. Diagnostics arrive in non-decreasing `span.from` (the
  // lint engine's sort is part of its contract), so a proposal that ends before the current
  // diagnostic begins can never match a later one either, and is passed for good.
  const cursor = new Map<string, number>();

  return diagnostics.map((diagnostic) => {
    if (diagnostic.fix !== undefined) return diagnostic;

    const bucket = byCode.get(diagnostic.code);
    if (bucket === undefined) return diagnostic;

    let at = cursor.get(diagnostic.code) ?? 0;
    while (at < bucket.length && (bucket[at]?.hi ?? 0) < diagnostic.span.from) at += 1;
    cursor.set(diagnostic.code, at);

    // `lo`/`hi` is only a necessary condition — a proposal whose *union* region misses the
    // diagnostic cannot have a span or an edit that hits it. Everything inside the window is
    // then tested exactly, so widening the filter cannot widen the answer.
    let only: Candidate | undefined;
    for (let i = at; i < bucket.length; i += 1) {
      const candidate = bucket[i];
      if (candidate === undefined || candidate.lo > diagnostic.span.to) break;
      if (candidate.hi < diagnostic.span.from) continue;
      if (!touches(candidate.proposal, diagnostic)) continue;

      // Two rules offering to close one diagnostic is not a fix, it is a choice. Stop here:
      // the answer cannot change and the scan has no reason to keep going.
      if (only !== undefined) return diagnostic;
      only = candidate;
    }
    if (only === undefined) return diagnostic;

    const fix: Fix = {
      ruleId: only.rule.id,
      labelCode: only.proposal.labelCode,
      ...(only.proposal.labelParams === undefined
        ? {}
        : { labelParams: only.proposal.labelParams }),
      edits: only.proposal.edits,
      confidence: only.rule.confidence,
    };

    return { ...diagnostic, fix };
  });
}

/**
 * Does this proposal act on the region this diagnostic points at?
 *
 * Half-open overlap, widened to touching at the endpoints, because the two layers anchor
 * the same defect at slightly different places by design: `syntax/close-brace-expected`
 * underlines the token where the brace was missed, while `close-open-containers` inserts at
 * the end of the document, and a zero-width insertion at offset `n` is "at" a diagnostic
 * that ends at `n`.
 */
function touches(proposal: RepairProposal, diagnostic: Diagnostic): boolean {
  const { from, to } = diagnostic.span;
  const overlaps = (a: number, b: number): boolean => a <= to && b >= from;

  if (overlaps(proposal.span.from, proposal.span.to)) return true;
  return proposal.edits.some((edit) => overlaps(edit.from, edit.to));
}
